import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { heroImage, themes } from "@/data/photographers";
import { magazineTopics } from "@/data/magazine-topics";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { pickWorks, getISOWeek, weekSeed, type SelectedItem } from "@/lib/selected-weeks";
import { fetchPicksForWeek } from "@/lib/selected-week-picks";
import { fetchApprovedCommunitySubmissions } from "@/lib/community-submissions";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";
import { transformedImageUrl, imageSrcSet } from "@/lib/image-url";
import { useTranslation } from "react-i18next";
import { getSettingText, SETTING_OG_HOME_IMAGE } from "@/lib/app-settings";

export const Route = createFileRoute("/")({
  loader: async () => {
    try {
      const now = new Date();
      const w = getISOWeek(now);
      const [picks, manualOg] = await Promise.all([
        fetchPicksForWeek(w.year, w.week),
        getSettingText(SETTING_OG_HOME_IMAGE, null),
      ]);
      const first = picks[0]?.image;
      // Manuell im Admin gesetzte Vorschau hat Vorrang, sonst Automatik.
      const ogImage = (manualOg && manualOg.trim()) || first || "https://omselected.art/og-cover.jpg";
      return {
        ogImage,
        heroPreload: first ? transformedImageUrl(first, { width: 1280, quality: 70, resize: "contain" }) : null,
      };
    } catch {
      return { ogImage: "https://omselected.art/og-cover.jpg", heroPreload: null };
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "OM Selected, Issue 01" },
      { name: "description", content: "Kuratiertes Magazin für Fotografie und Make-up. Bild des Tages, Selected Works, ausgewählte Fotograf:innen und Visagist:innen." },
      { property: "og:title", content: "OM Selected, Issue 01" },
      { property: "og:description", content: "Kuratiertes Magazin für Fotografie und Make-up." },
      { property: "og:url", content: "https://omselected.art/" },
      { property: "og:image", content: loaderData?.ogImage ?? "https://omselected.art/og-cover.jpg" },
      { name: "twitter:image", content: loaderData?.ogImage ?? "https://omselected.art/og-cover.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://omselected.art/" },
      ...(loaderData?.heroPreload
        ? [{ rel: "preload", as: "image", href: loaderData.heroPreload, fetchPriority: "high" as const }]
        : []),
    ],
  }),
  component: Index,
});

function Index() {
  const { t, i18n } = useTranslation();
  const [weekPicks, setWeekPicks] = useState<SelectedItem[]>([]);
  const [selectedPicks, setSelectedPicks] = useState<SelectedItem[]>([]);
  const [weekIndex, setWeekIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentWeek = useMemo(() => getISOWeek(new Date()), []);
  const [lightbox, setLightbox] = useState<LightboxInfo | null>(null);
  const [themeImages, setThemeImages] = useState<Record<string, string[]>>({});
  const [themeIndex, setThemeIndex] = useState(0);
  const openLightbox = (w: SelectedItem) => setLightbox({
    image: w.image,
    caption: w.caption,
    photographerName: w.photographerName,
    photographerId: w.photographerId,
    region: w.region ?? null,
    theme: w.theme ?? null,
    instagramHandle: w.instagramHandle ?? null,
    websiteUrl: w.websiteUrl ?? null,
  });

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchAllPhotographers(),
      fetchPicksForWeek(currentWeek.year, currentWeek.week),
      fetchApprovedCommunitySubmissions(200),
    ]).then(([all, curated, community]) => {
      if (!active) return;
      const withGallery = all.filter((p) => p.gallery.length > 0);
      // Community-Einreichungen dieser KW (freigegeben)
      const communityThisWeek: SelectedItem[] = community
        .filter((c) => {
          const d = new Date(c.reviewed_at ?? c.created_at);
          const w = getISOWeek(d);
          return w.year === currentWeek.year && w.week === currentWeek.week;
        })
        .map((c) => ({
          id: `c-${c.id}`,
          image: c.image_url,
          caption: c.caption ?? c.submitter_name,
          photographerId: "",
          photographerName: c.submitter_name,
          region: c.region ?? undefined,
          instagramHandle: c.instagram_handle ?? undefined,
          websiteUrl: c.website_url ?? undefined,
        }));
      // Hero: 5 zufällige Bilder, gemischt aus Selected (kuratiert + Community) und Themes (Galerien)
      const themePool: SelectedItem[] = [];
      withGallery.forEach((p) => {
        p.gallery.forEach((src, i) => {
          themePool.push({
            id: `t-${p.id}-${i}`,
            image: src,
            caption: p.name,
            photographerId: p.id,
            photographerName: p.name,
            region: p.region ?? undefined,
            theme: p.galleryThemes?.[i] ?? p.themes?.[0],
            instagramHandle: p.instagramHandle ?? undefined,
            websiteUrl: p.websiteUrl ?? undefined,
          });
        });
      });
      const heroPool = [...curated, ...communityThisWeek, ...themePool];
      const shuffledHero = [...heroPool].sort(() => Math.random() - 0.5);
      const seenHeroImage = new Set<string>();
      const seenHeroPhotographer = new Set<string>();
      const heroRandom = shuffledHero
        .filter((it) => {
          if (seenHeroImage.has(it.image)) return false;
          const key = it.photographerId || it.photographerName;
          if (key && seenHeroPhotographer.has(key)) return false;
          seenHeroImage.add(it.image);
          if (key) seenHeroPhotographer.add(key);
          return true;
        })
        .slice(0, 5);
      setWeekPicks(
        heroRandom.length > 0
          ? heroRandom
          : pickWorks(withGallery, 5, weekSeed(currentWeek.year, currentWeek.week)),
      );
      // Selected-Section: Member + Community dieser KW
      const combined = [...curated, ...communityThisWeek];
      setSelectedPicks(
        combined.length > 0
          ? combined
          : pickWorks(withGallery, 5, weekSeed(currentWeek.year, currentWeek.week)),
      );
      const byTheme: Record<string, string[]> = {};
      all.forEach((p) => {
        p.gallery.forEach((src, i) => {
          const ts = p.galleryThemesAll?.[i] ?? [p.galleryThemes?.[i] ?? p.themes?.[0] ?? "open"];
          ts.forEach((t) => {
            if (!byTheme[t]) byTheme[t] = [];
            byTheme[t].push(src);
          });
        });
      });
      Object.keys(byTheme).forEach((k) => {
        byTheme[k] = [...byTheme[k]].sort(() => Math.random() - 0.5).slice(0, 6);
      });
      setThemeImages(byTheme);
    });
    return () => { active = false; };
  }, [currentWeek.year, currentWeek.week]);

  useEffect(() => {
    if (weekPicks.length < 2) return;
    const id = setInterval(() => setWeekIndex((i) => (i + 1) % weekPicks.length), 4500);
    return () => clearInterval(id);
  }, [weekPicks.length]);

  useEffect(() => {
    if (selectedPicks.length < 2) return;
    const id = setInterval(() => setSelectedIndex((i) => (i + 1) % selectedPicks.length), 4500);
    return () => clearInterval(id);
  }, [selectedPicks.length]);

  useEffect(() => {
    const id = setInterval(() => setThemeIndex((i) => i + 1), 3500);
    return () => clearInterval(id);
  }, []);

  const activePick = weekPicks[weekIndex];
  const featured = selectedPicks[selectedIndex];
  // Fixed aspect ratios to prevent layout shift between image swaps.
  const heroAspect = "aspect-[4/5] md:aspect-[16/10]";
  const featuredAspect = "aspect-[4/5]";

  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="pt-12 md:pt-20 pb-16 md:pb-24 px-5 md:px-8">
        <div className="mx-auto max-w-[1280px]">
          <div className="grid lg:grid-cols-12 gap-10 md:gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="eyebrow text-muted-foreground block mb-6">{t("home.eyebrow")}</span>
              <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight mb-8">
                {t("home.heroTitlePre")}<em className="italic">{t("home.heroTitleEm")}</em>{t("home.heroTitlePost")}
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md mb-10">
                {t("home.heroLead")}
              </p>
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link
                  to="/brief"
                  className="bg-foreground text-background px-8 py-4 hover:bg-foreground/85 transition-colors"
                >{t("home.ctaBrief")}</Link>
                <Link
                  to="/photographers"
                  className="border border-foreground/20 px-8 py-4 hover:bg-muted transition-colors"
                >{t("home.ctaPhotographers")}</Link>
              </div>
              <p className="mt-6 eyebrow italic text-muted-foreground">
                {t("home.trustline")}
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className={`relative ${heroAspect} overflow-hidden bg-transparent transition-[aspect-ratio] duration-500`}>
                {weekPicks.length === 0 ? (
                  <img
                    src={heroImage}
                    alt="OM Selected, Cover"
                    fetchPriority="high"
                    width={1280}
                    height={800}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  weekPicks.map((w, i) => (
                    <img
                      key={w.id}
                      src={transformedImageUrl(w.image, { width: 1280, quality: 70, resize: "contain" })}
                      srcSet={imageSrcSet(w.image, [640, 960, 1280, 1600], { quality: 70, resize: "contain" })}
                      sizes="(min-width: 1024px) 740px, 100vw"
                      alt={w.caption ? `Fotografie: ${w.caption}` : `Fotografie von ${w.photographerName}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : "auto"}
                      width={1280}
                      height={800}
                      onClick={() => i === weekIndex && openLightbox(w)}
                      onError={(e) => { const img = e.currentTarget; if (img.src !== w.image) img.src = w.image; }}
                      className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-in-out ${i === weekIndex ? "cursor-zoom-in opacity-100" : "opacity-0 pointer-events-none"}`}
                    />
                  ))
                )}
              </div>
              <div className="mt-4 flex flex-col items-center gap-3">
                <span className="eyebrow text-muted-foreground text-[10px] tracking-widest">
                  {t("home.weekImage", { week: currentWeek.week })}
                </span>
                {weekPicks.length > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    {weekPicks.map((w, i) => (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => setWeekIndex(i)}
                        aria-label={t("home.dotAria", { n: i + 1 })}
                        className={`h-1 transition-all ${i === weekIndex ? "w-10 bg-foreground" : "w-4 bg-border hover:bg-muted-foreground"}`}
                      />
                    ))}
                  </div>
                )}
                {activePick && (
                    <Link
                      to="/photographers/$id"
                      params={{ id: activePick.photographerId }}
                      className="text-xs italic text-muted-foreground hover:text-foreground text-center"
                    >
                      {activePick.photographerName}
                      {activePick.region
                        ? `, ${activePick.region}`
                        : activePick.caption && activePick.caption !== activePick.photographerName
                          ? `, ${activePick.caption}`
                          : ""}
                    </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-muted py-20 md:py-24 px-5 md:px-8 border-y border-border">
        <div className="mx-auto max-w-[1280px]">
          <div className="flex flex-wrap justify-between items-baseline gap-4 mb-14 md:mb-16">
            <h2 className="serif text-3xl md:text-4xl italic">{t("home.howTitle")}</h2>
            <div className="h-px flex-grow mx-2 md:mx-8 bg-border hidden md:block"></div>
            <span className="eyebrow text-muted-foreground">{t("home.howSubtitle")}</span>
          </div>
          <div className="grid md:grid-cols-3 gap-12 md:gap-16">
            {[
              { n: "01", title: t("home.step1Title"), desc: t("home.step1Desc") },
              { n: "02", title: t("home.step2Title"), desc: t("home.step2Desc") },
              { n: "03", title: t("home.step3Title"), desc: t("home.step3Desc") },
            ].map((s) => (
              <div key={s.n} className="group">
                <div className="serif text-5xl mb-6 text-muted-foreground group-hover:text-foreground transition-colors">{s.n}</div>
                <h3 className="eyebrow text-base font-medium mb-3 normal-case tracking-wider">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/brief" className="eyebrow border-b border-foreground pb-1 hover:opacity-60 transition-opacity">
              {t("home.howCta")}
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Selected Work */}
      {featured && (
        <section className="py-24 md:py-32 px-5 md:px-8">
          <div className="mx-auto max-w-[1280px]">
            <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
              <div className="col-span-12 md:col-span-4 md:sticky md:top-32">
                <span className="eyebrow text-muted-foreground block mb-4">{t("home.selectedKW", { week: currentWeek.week })}</span>
                <h2 className="serif text-4xl md:text-5xl mb-8 leading-tight">
                  {featured.caption || featured.photographerName}
                </h2>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  {t("home.selectedLead", { name: featured.photographerName })}
                </p>
                <Link
                  to="/photographers/$id"
                  params={{ id: featured.photographerId }}
                  className="eyebrow inline-block border-b border-foreground pb-2 hover:opacity-60 transition-opacity"
                >{t("home.viewPhotographer", { name: featured.photographerName })}</Link>
                <div className="mt-3 block">
                  <Link to="/selected" className="eyebrow text-muted-foreground hover:text-foreground">
                    {t("home.allSelected")}
                  </Link>
                </div>
              </div>
              <div className="col-span-12 md:col-span-8">
                <button
                  type="button"
                  onClick={() => openLightbox(featured)}
                  className={`block w-full cursor-zoom-in relative ${featuredAspect} bg-transparent overflow-hidden`}
                  aria-label={t("home.zoomAria")}
                >
                  <img
                    src={transformedImageUrl(featured.image, { width: 1280, quality: 72, resize: "contain" })}
                    srcSet={imageSrcSet(featured.image, [640, 960, 1280, 1600], { quality: 72, resize: "contain" })}
                    sizes="(min-width: 768px) 800px, 100vw"
                    alt={featured.caption ? `Fotografie: ${featured.caption}` : `Fotografie von ${featured.photographerName}`}
                    loading="lazy"
                    onError={(e) => { const img = e.currentTarget; if (img.src !== featured.image) img.src = featured.image; }}
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Themes */}
      <section className="py-20 md:py-24 px-5 md:px-8 bg-[oklch(0.16_0_0)] text-background">
        <div className="mx-auto max-w-[1280px] text-center mb-14 md:mb-16">
          <h2 className="serif text-3xl md:text-4xl mb-4">{t("home.themesTitle")}</h2>
          <p className="eyebrow opacity-50">{t("home.themesSubtitle")}</p>
        </div>
        <div className="mx-auto max-w-[1280px] grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {themes.map((th) => {
            const label = t(`themesData.${th.id}.label`, th.label);
            const description = t(`themesData.${th.id}.description`, th.description);
            return (
            <Link
              key={th.id}
              to="/themes/$theme"
              params={{ theme: th.id }}
              className="group block"
            >
              <div className="relative aspect-[3/4] bg-[oklch(0.27_0_0)] mb-6 overflow-hidden">
                {themeImages[th.id]?.length ? (
                  themeImages[th.id].map((src, i) => (
                    <img
                      key={src + i}
                        src={transformedImageUrl(src, { width: 700, height: 934, quality: 78, resize: "cover" })}
                        srcSet={[400, 560, 700, 900]
                          .map((w) => `${transformedImageUrl(src, { width: w, height: Math.round((w * 4) / 3), quality: 78, resize: "cover" })} ${w}w`)
                          .join(", ")}
                        sizes="(min-width: 1024px) 280px, 45vw"
                      alt={`${label} photography`}
                      loading="lazy"
                        onError={(e) => { const img = e.currentTarget; if (img.src !== src) { img.srcset = ""; img.src = src; } }}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === themeIndex % themeImages[th.id].length ? "opacity-100" : "opacity-0"}`}
                    />
                  ))
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[oklch(0.35_0_0)] to-[oklch(0.16_0_0)] flex items-center justify-center">
                    <span className="serif text-7xl opacity-20 group-hover:opacity-40 transition-opacity">{label[0]}</span>
                  </div>
                )}
              </div>
              <h3 className="serif text-2xl mb-2">{label}</h3>
              <p className="eyebrow opacity-50">{description}</p>
            </Link>
            );
          })}
        </div>
        <div className="mx-auto max-w-[1280px] mt-14 text-center">
          <Link
            to="/brief"
            className="eyebrow inline-block border border-background/30 px-8 py-4 hover:bg-background hover:text-foreground transition-colors"
          >{t("home.themesCta")}</Link>
        </div>
      </section>

      {/* Magazine guides */}
      <section className="py-20 md:py-24 px-5 md:px-8 border-b border-border">
        <div className="mx-auto max-w-[1280px]">
          <div className="flex flex-wrap justify-between items-baseline gap-4 mb-12">
            <h2 className="serif text-3xl md:text-4xl">{t("home.magazinTitle")}</h2>
            <span className="eyebrow text-muted-foreground">{t("home.magazinSubtitle")}</span>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {magazineTopics.map((m) => (
              <Link
                key={m.slug}
                to="/magazin/$topic"
                params={{ topic: m.slug }}
                className="group block border border-border p-8 hover:bg-muted transition-colors"
              >
                <span className="eyebrow text-muted-foreground block mb-4">{t(`magazineCards.${m.slug}.eyebrow`, m.eyebrow)}</span>
                <h3 className="serif text-2xl mb-3 leading-tight">{t(`magazineCards.${m.slug}.label`, m.label)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(`magazineCards.${m.slug}.description`, m.metaDescription)}</p>
                <span className="eyebrow inline-block mt-6 border-b border-foreground pb-1 group-hover:opacity-60 transition-opacity">
                  {t("home.readMore")} {t(`magazineCards.${m.slug}.label`, m.label)}
                </span>
              </Link>
            ))}
            <Link
              to="/magazin/tipps-amateur-models"
              className="group block border border-border p-8 hover:bg-muted transition-colors"
            >
              <span className="eyebrow text-muted-foreground block mb-4">
                {i18n.language === "en" ? "Magazine, Models" : "Magazin, Models"}
              </span>
              <h3 className="serif text-2xl mb-3 leading-tight">
                {i18n.language === "en" ? "Tips & Tricks for Amateur Models" : "Tipps & Tricks für Amateur Models"}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {i18n.language === "en"
                  ? "How to prepare, pose naturally, master expression and stay relaxed in front of the camera."
                  : "So bereitest du dich vor, posierst natürlich, meisterst Mimik und bleibst vor der Kamera entspannt."}
              </p>
              <span className="eyebrow inline-block mt-6 border-b border-foreground pb-1 group-hover:opacity-60 transition-opacity">
                {t("home.readMore")} {i18n.language === "en" ? "Tips for Models" : "Model-Tipps"}
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Closing Quote */}
      <section className="py-24 md:py-32 px-5 md:px-8">
        <div className="mx-auto max-w-[1280px] mb-24 md:mb-32 border-y border-border py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <span className="eyebrow text-muted-foreground block mb-5">{t("home.creativesEyebrow")}</span>
              <h2 className="serif text-4xl md:text-5xl leading-[1] mb-6">
                {t("home.creativesTitlePre")}<em className="italic">{t("home.creativesTitleEm")}</em>{t("home.creativesTitlePost")}
              </h2>
              <p className="text-muted-foreground max-w-lg leading-relaxed">
                {t("home.creativesLead")}
              </p>
              <p className="mt-5 eyebrow italic text-foreground/70">
                {t("home.creativesFounding")}
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link
                  to="/apply"
                  className="bg-foreground text-background px-8 py-4 hover:bg-foreground/85 transition-colors"
                >
                  {t("home.applyPhotographer")}
                </Link>
                <Link
                  to="/apply"
                  search={{ as: "visagist" }}
                  className="border border-foreground/20 px-8 py-4 hover:bg-muted transition-colors"
                >
                  {t("home.applyVisagist")}
                </Link>
                <Link
                  to="/about"
                  className="border border-foreground/20 px-8 py-4 hover:bg-muted transition-colors"
                >
                  {t("home.learnMore")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[800px] text-center">
          <blockquote className="serif text-3xl md:text-5xl leading-tight italic opacity-90">
            {t("home.quote")}
          </blockquote>
          <div className="h-12 w-px bg-border mx-auto my-14"></div>
          <Link
            to="/brief"
            className="eyebrow inline-block bg-foreground text-background px-10 py-4 hover:bg-foreground/85 transition-colors"
          >{t("home.finalCta")}</Link>
        </div>
      </section>
      <PhotoLightbox info={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
